(() => {
  if (window.AbcProductFreeGift) return;
  window.AbcProductFreeGift = true;

  const ROOT_SELECTOR = "[data-abc-product-gift]";
  const MODAL_SELECTOR = "[data-abc-add-more-product-modal]";
  const GIFT_MODAL_SELECTOR = "[data-abc-product-gift-modal]";
  const FREE_GIFT_HANDLE_PREFIX = "free-gift-";
  const ANIMATION_MS = 280;
  let giftAddInFlight = false;
  let giftDedupePromise = null;

  function routes() {
    return (window.MinimogSettings && window.MinimogSettings.routes) || {};
  }

  function money(cents) {
    if (typeof window.formatMoney === "function") {
      const settings = window.MinimogSettings || {};
      const format = settings.money_with_currency_format || settings.money_format;
      const holder = document.createElement("div");
      holder.innerHTML = window.formatMoney(Number(cents || 0), format);
      return (holder.textContent || "").replace(/\s+/g, " ").trim();
    }

    const code = (window.MinimogSettings && window.MinimogSettings.currency_code) || "USD";
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return code === "USD" ? `US$${amount}` : `${code} ${amount}`;
  }

  function fetchConfig() {
    return {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    };
  }

  async function fetchCart() {
    const cartRoute = routes().cart || routes().cart_url || "/cart";
    const cartJsonRoute = String(cartRoute).endsWith(".js") ? cartRoute : `${String(cartRoute).replace(/\/$/, "")}.js`;
    const response = await fetch(cartJsonRoute, {
      credentials: "same-origin",
      headers: { "Accept": "application/json" }
    });
    if (!response.ok) throw new Error(`Unable to load cart (${response.status})`);

    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (error) {
      throw new Error("Unable to parse cart");
    }
  }

  async function changeCartLine(id, quantity) {
    const response = await fetch(routes().cart_change_url || "/cart/change.js", {
      ...fetchConfig(),
      body: JSON.stringify({ id, quantity })
    });
    if (!response.ok) throw new Error("Unable to update cart");
    return response.json();
  }

  function itemHasFreeGiftProperty(item) {
    const properties = item && item.properties;
    if (!properties) return false;

    if (Array.isArray(properties)) {
      return properties.some((property) => {
        return property && property.name === "_free_gift" && String(property.value) === "true";
      });
    }

    return String(properties._free_gift || "") === "true";
  }

  function isFreeGiftHandle(handle) {
    return String(handle || "").toLowerCase().startsWith(FREE_GIFT_HANDLE_PREFIX);
  }

  function isFreeGiftTitle(title) {
    return String(title || "").trim().toLowerCase().startsWith("free gift");
  }

  function isFreeGiftCartItem(item) {
    if (!item) return false;
    return itemHasFreeGiftProperty(item) || isFreeGiftHandle(item.handle) || isFreeGiftTitle(item.product_title || item.title);
  }

  function cartFreeGiftItems(cart) {
    return cart && Array.isArray(cart.items) ? cart.items.filter(isFreeGiftCartItem) : [];
  }

  function cartHasFreeGift(cart) {
    return cartFreeGiftItems(cart).length > 0;
  }

  async function enforceSingleFreeGift(cart) {
    if (giftDedupePromise) return giftDedupePromise;

    giftDedupePromise = (async () => {
      const currentCart = cart || await fetchCart();
      const gifts = cartFreeGiftItems(currentCart);
      if (!gifts.length) return currentCart;

      let changed = false;
      const keeper = gifts[0];
      if (Number(keeper.quantity || 1) > 1 && keeper.key) {
        await changeCartLine(keeper.key, 1);
        changed = true;
      }

      for (const item of gifts.slice(1)) {
        if (!item.key) continue;
        await changeCartLine(item.key, 0);
        changed = true;
      }

      if (!changed) return currentCart;
      const cleanedCart = await fetchCart();
      document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: true } }));
      return cleanedCart;
    })();

    try {
      return await giftDedupePromise;
    } finally {
      giftDedupePromise = null;
    }
  }

  function getThreshold(root) {
    return Number(root.dataset.giftThreshold || 4900);
  }

  function isUnlocked(root) {
    return Boolean(root && root.classList.contains("is-unlocked"));
  }

  function hasFreeGift(root) {
    return Boolean(root && root.classList.contains("has-free-gift"));
  }

  function giftModalFor(root) {
    if (!root) return null;
    return root._abcGiftModal || root.querySelector(GIFT_MODAL_SELECTOR);
  }

  function queryOneInRoot(root, selector) {
    if (!root) return null;
    const local = root.querySelector(selector);
    if (local) return local;

    const modal = giftModalFor(root);
    return modal && !root.contains(modal) ? modal.querySelector(selector) : null;
  }

  function queryAllInRoot(root, selector) {
    if (!root) return [];

    const nodes = Array.from(root.querySelectorAll(selector));
    const modal = giftModalFor(root);
    if (modal && !root.contains(modal)) {
      nodes.push(...Array.from(modal.querySelectorAll(selector)));
    }
    return nodes;
  }

  function rootForTarget(target) {
    if (!target || !target.closest) return null;
    const root = target.closest(ROOT_SELECTOR);
    if (root) return root;

    const modal = target.closest(GIFT_MODAL_SELECTOR);
    return modal && modal._abcGiftRoot ? modal._abcGiftRoot : null;
  }

  function rootForNode(node) {
    return rootForTarget(node) || (node && node.closest ? node.closest(ROOT_SELECTOR) : null);
  }

  function isCurrentProductFreeGift(root) {
    if (!root) return false;
    return isFreeGiftHandle(root.dataset.currentProductHandle) || isFreeGiftTitle(root.dataset.currentProductTitle);
  }

  function setProductFormGiftState(root, blocked) {
    if (!isCurrentProductFreeGift(root)) return;

    document.querySelectorAll("product-form .m-add-to-cart[name='add']").forEach((button) => {
      const text = button.querySelector(".m-add-to-cart--text");
      if (!button.dataset.abcGiftOriginalDisabled) {
        button.dataset.abcGiftOriginalDisabled = button.disabled ? "true" : "false";
      }
      if (text && !button.dataset.abcGiftOriginalText) {
        button.dataset.abcGiftOriginalText = text.textContent.trim();
      }

      if (blocked) {
        button.disabled = true;
        button.classList.add("is-disabled-by-gift");
        if (text) text.textContent = "Gift Selected";
      } else if (button.dataset.abcGiftOriginalDisabled === "false") {
        button.disabled = false;
        button.classList.remove("is-disabled-by-gift");
        if (text && button.dataset.abcGiftOriginalText) text.textContent = button.dataset.abcGiftOriginalText;
      }
    });

    document.querySelectorAll(".m-product-dynamic-checkout").forEach((node) => {
      node.toggleAttribute("hidden", blocked);
    });

    const message = document.querySelector(".m-product-form-message");
    if (message && blocked) {
      message.textContent = "Free gift selected. One free gift per order.";
      message.classList.add("notification", "show", "warning");
    }
  }

  async function loadMoreGifts(root, button) {
    if (button.disabled) return;
    const panel = button.closest('[data-abc-product-gift-panel]');
    const controls = button.closest('[data-abc-gift-pagination]');
    const status = controls.querySelector('[data-abc-gift-load-status]');
    const url = new URL(panel.dataset.giftCollectionUrl, window.location.origin);
    url.searchParams.set('view', 'product-gift');
    url.searchParams.set('page', button.dataset.nextPage);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    button.disabled = true;
    button.textContent = 'Loading...';
    panel.setAttribute('aria-busy', 'true');
    status.textContent = '';
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Gift page request failed');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const page = parsed.querySelector('[data-abc-gift-page]');
      if (!page) throw new Error('Invalid gift page');
      const nodes = Array.from(page.children).filter(node => node.matches('.abc-product-gift__card, [data-abc-add-more-product-modal]'));
      const next = page.querySelector('[data-abc-gift-next-url]');
      if (!nodes.length && next) throw new Error('Empty gift page');
      let nextPage;
      if (next) {
        const nextUrl = new URL(next.dataset.abcGiftNextUrl, window.location.origin);
        nextPage = Number(nextUrl.searchParams.get('page'));
        if (!(nextPage > Number(button.dataset.nextPage))) throw new Error('Invalid next page');
      }
      nodes.forEach(node => panel.insertBefore(document.importNode(node, true), controls));
      setButtonState(root, isUnlocked(root), hasFreeGift(root));
      if (next) {
        button.dataset.nextPage = String(nextPage);
      } else {
        button.hidden = true;
        status.textContent = 'All gifts loaded.';
      }
    } catch (error) {
      status.textContent = 'Could not load more gifts. Please try again.';
    } finally {
      window.clearTimeout(timeout);
      button.disabled = false;
      button.textContent = 'Load more';
      panel.removeAttribute('aria-busy');
    }
  }

  function setNotice(root, text) {
    const notice = queryOneInRoot(root, "[data-abc-product-gift-notice]");
    if (!notice) return;
    notice.textContent = text || "";
    notice.classList.toggle("is-visible", Boolean(text));
  }

  function openGiftModal(root) {
    const modal = giftModalFor(root);
    if (!modal) return;

    modal._abcGiftRoot = root;
    root._abcGiftModal = modal;
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    window.clearTimeout(modal._abcGiftListCloseTimer);
    modal.classList.remove("is-closing");
    modal.classList.add("is-open", "m-cart-drawer--active");
    modal.setAttribute("aria-hidden", "false");

    const closeButton = modal.querySelector("[data-abc-product-gift-close]");
    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  function closeGiftModal(root) {
    const modal = giftModalFor(root);
    if (!modal) return;

    window.clearTimeout(modal._abcGiftListCloseTimer);
    if (!modal.classList.contains("is-open")) {
      modal.classList.remove("is-closing", "m-cart-drawer--active");
      modal.setAttribute("aria-hidden", "true");
      return;
    }

    modal.classList.add("is-closing");
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    modal._abcGiftListCloseTimer = window.setTimeout(() => {
      modal.classList.remove("is-closing", "m-cart-drawer--active");
      modal._abcGiftListCloseTimer = null;
    }, ANIMATION_MS);
  }

  function setPanelState(root, unlocked) {
    queryAllInRoot(root, "[data-abc-product-gift-panel]").forEach((panel) => {
      const mode = panel.getAttribute("data-abc-product-gift-panel");
      panel.hidden = unlocked ? mode !== "free" : mode !== "paid";
    });
  }

  function setButtonState(root, unlocked, freeGiftSelected) {
    queryAllInRoot(root, "[data-abc-product-gift-button], [data-abc-product-gift-submit]").forEach((button) => {
      const freeMode = button.dataset.giftFreeMode === "true";
      const shouldBlock = freeMode && freeGiftSelected;
      button.disabled = shouldBlock;
      button.classList.toggle("is-disabled-by-gift", shouldBlock);
      if (freeMode) {
        button.setAttribute("aria-label", shouldBlock ? "Free gift already selected" : "Add free gift to cart");
      }
      if (button.matches("[data-abc-product-gift-submit]")) {
        button.textContent = freeMode ? (shouldBlock ? "Gift Selected" : "Add Free Gift") : "Add To Cart";
      }
    });

    queryAllInRoot(root, "[data-abc-product-gift-link]").forEach((link) => {
      link.classList.toggle("is-disabled-by-gift", freeGiftSelected);
      link.setAttribute("aria-disabled", freeGiftSelected ? "true" : "false");
      if (freeGiftSelected) {
        link.setAttribute("tabindex", "-1");
      } else {
        link.removeAttribute("tabindex");
      }
    });

    queryAllInRoot(root, "[data-abc-product-gift-price]").forEach((price) => {
      const card = price.closest(".abc-product-gift__card");
      const freeModeButton = card && card.querySelector("[data-gift-free-mode='true']");
      const originalCents = Number(price.dataset.originalPrice || 0);
      if (freeModeButton && unlocked) {
        price.innerHTML = `<strong>FREE</strong><s>${money(originalCents)}</s>`;
      } else if (!price.dataset.originalHtml) {
        price.dataset.originalHtml = price.innerHTML;
      } else {
        price.innerHTML = price.dataset.originalHtml;
      }
    });
  }

  function updateCopy(root, cart) {
    const threshold = getThreshold(root);
    const total = Number((cart && cart.total_price) || root.dataset.cartTotal || 0);
    const unlocked = total >= threshold;
    const freeGiftSelected = cartHasFreeGift(cart);
    const away = Math.max(0, threshold - total);
    const message = root.querySelector("[data-abc-product-gift-message]");
    const modalMessage = queryOneInRoot(root, "[data-abc-product-gift-modal-message]");
    const modalTitle = queryOneInRoot(root, "[data-abc-product-gift-title]");

    root.dataset.cartTotal = String(total);
    root.classList.toggle("is-unlocked", unlocked);
    root.classList.toggle("has-free-gift", freeGiftSelected);
    setPanelState(root, unlocked);
    setButtonState(root, unlocked, freeGiftSelected);
    setProductFormGiftState(root, freeGiftSelected);

    let title = "Gift Ideas";
    let messageHtml = `Add <strong data-abc-product-gift-away>${money(away)}</strong> more to unlock a free gift.`;

    if (freeGiftSelected) {
      title = "Free Gift Selected";
      messageHtml = "Free gift selected. One free gift per order.";
    } else if (unlocked) {
      title = "Choose Your Free Gift";
      messageHtml = "Choose one free gift and add it to your cart.";
    }

    if (message) message.innerHTML = messageHtml;
    if (modalMessage) modalMessage.innerHTML = messageHtml;
    if (modalTitle) modalTitle.textContent = title;
  }

  function updateAllRoots(cart) {
    const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));
    if (!roots.length) return;

    roots.forEach((root) => updateCopy(root, cart));
  }

  async function refreshAll(cart) {
    const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));
    if (!roots.length) return;

    if (cart && Array.isArray(cart.items)) {
      updateAllRoots(cart);
      return;
    }

    try {
      updateAllRoots(await enforceSingleFreeGift(await fetchCart()));
    } catch (error) {
      console.warn("Unable to refresh free gift status:", error);
    }
  }

  function closeModal(modal) {
    if (!modal) return;
    window.clearTimeout(modal._abcGiftCloseTimer);

    if (!modal.classList.contains("is-open")) {
      modal.classList.remove("is-closing");
      modal.setAttribute("aria-hidden", "true");
      return;
    }

    modal.classList.add("is-closing");
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    modal._abcGiftCloseTimer = window.setTimeout(() => {
      modal.classList.remove("is-closing");
      modal._abcGiftCloseTimer = null;
    }, ANIMATION_MS);
  }

  function closeAllModals(root) {
    if (!root) {
      document.querySelectorAll(MODAL_SELECTOR).forEach(closeModal);
      return;
    }
    queryAllInRoot(root, MODAL_SELECTOR).forEach(closeModal);
  }

  function parseVariantData(modal) {
    if (!modal) return [];
    if (modal._abcGiftVariantData) return modal._abcGiftVariantData;

    const script = modal.querySelector("[data-abc-add-more-variants]");
    try {
      modal._abcGiftVariantData = JSON.parse(script ? script.textContent : "[]");
    } catch (error) {
      modal._abcGiftVariantData = [];
    }
    return modal._abcGiftVariantData;
  }

  function selectedOptions(modal) {
    return Array.from(modal.querySelectorAll("[data-abc-add-more-option]"))
      .sort((a, b) => Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0))
      .map((group) => {
        const active = group.querySelector("[data-abc-add-more-option-value].is-active");
        return active ? active.dataset.optionValue : "";
      });
  }

  function selectedVariant(modal) {
    const options = selectedOptions(modal);
    return parseVariantData(modal).find((variant) => {
      return options.every((value, index) => !value || variant.options[index] === value);
    });
  }

  function updateModalState(modal) {
    if (!modal) return;

    const variants = parseVariantData(modal);
    const options = selectedOptions(modal);
    modal.querySelectorAll("[data-abc-add-more-option]").forEach((group) => {
      const index = Number(group.dataset.optionPosition || 1) - 1;
      const active = group.querySelector("[data-abc-add-more-option-value].is-active");
      const label = group.querySelector("[data-abc-add-more-option-selected]");
      if (active && label) label.textContent = active.dataset.optionValue || "";

      group.querySelectorAll("[data-abc-add-more-option-value]").forEach((button) => {
        const testOptions = options.slice();
        testOptions[index] = button.dataset.optionValue;
        const available = variants.some((variant) => {
          return variant.available && testOptions.every((value, optionIndex) => !value || variant.options[optionIndex] === value);
        });
        button.classList.toggle("is-unavailable", !available);
        button.disabled = !available;
      });
    });

    const variant = selectedVariant(modal);
    const submit = modal.querySelector("[data-abc-product-gift-submit]");
    const price = modal.querySelector("[data-abc-product-gift-modal-price]");
    const freeMode = submit && submit.dataset.giftFreeMode === "true";
    const root = rootForNode(modal);

    if (submit) {
      submit.dataset.selectedVariant = variant ? variant.id : "";
      submit.disabled = !variant || !variant.available || (freeMode && hasFreeGift(root));
    }

    if (price && variant) {
      price.dataset.originalPrice = String(variant.price);
      price.textContent = freeMode && isUnlocked(root) ? "FREE" : money(variant.price);
    }
  }

  function openModal(root, trigger) {
    const modal = document.getElementById(trigger.dataset.modalId || "");
    const giftModal = giftModalFor(root);
    if (!modal || (!root.contains(modal) && !(giftModal && giftModal.contains(modal)))) return;

    closeAllModals(root);
    window.clearTimeout(modal._abcGiftCloseTimer);
    modal.classList.remove("is-closing");
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    updateModalState(modal);

    const closeButton = modal.querySelector("[data-abc-add-more-product-close]");
    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  async function addVariant(root, variantId, control, freeMode) {
    if (!variantId) return;
    if (freeMode && giftAddInFlight) {
      setNotice(root, "Only one free gift can be added per order.");
      return;
    }

    setNotice(root, "");
    if (control) control.disabled = true;
    if (freeMode) giftAddInFlight = true;

    try {
      let cart = null;
      if (freeMode && hasFreeGift(root)) {
        setNotice(root, "Only one free gift can be added per order.");
        return;
      }

      if (freeMode && !isUnlocked(root)) {
        setNotice(root, "Add more items to unlock your free gift.");
        return;
      }

      try {
        cart = await enforceSingleFreeGift(await fetchCart());
        updateCopy(root, cart);
      } catch (error) {
        console.warn("Unable to validate free gift status before adding:", error);
      }

      if (freeMode && cart && cartHasFreeGift(cart)) {
        setNotice(root, "Only one free gift can be added per order.");
        return;
      }

      if (freeMode && cart && Number(cart.total_price || 0) < getThreshold(root)) {
        setNotice(root, "Add more items to unlock your free gift.");
        return;
      }

      const properties = freeMode
        ? {
            "_free_gift": "true",
            "_free_gift_threshold": "49",
            "_free_gift_collection_id": root.dataset.freeCollectionId || ""
          }
        : {
            "_gift_offer": "regular_price",
            "_gift_collection_id": root.dataset.paidCollectionId || ""
          };

      const response = await fetch(routes().cart_add_url || "/cart/add.js", {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties
            }
          ]
        })
      });

      if (!response.ok) throw new Error("Unable to add gift");
      await response.json();
      if (freeMode) {
        root.classList.add("is-unlocked", "has-free-gift");
        setPanelState(root, true);
        setButtonState(root, true, true);
        setProductFormGiftState(root, true);
        setNotice(root, "");
      }
      try {
        await enforceSingleFreeGift();
      } catch (error) {
        console.warn("Unable to verify free gift after adding:", error);
      }
      closeAllModals(root);
      closeGiftModal(root);
      document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: true } }));
      window.setTimeout(refreshAll, 500);
    } catch (error) {
      console.error("Failed to add gift product:", error);
      setNotice(root, "Gift could not be added. Please try again.");
    } finally {
      if (freeMode) giftAddInFlight = false;
      if (control) control.disabled = false;
    }
  }

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!form || !form.matches || !form.matches("form[data-type='add-to-cart-form']")) return;

    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || !isCurrentProductFreeGift(root) || !hasFreeGift(root)) return;

    event.preventDefault();
    event.stopPropagation();
    setProductFormGiftState(root, true);
    setNotice(root, "Free gift selected. One free gift per order.");
  }, true);

  document.addEventListener("click", (event) => {
    const target = event.target && event.target.closest ? event.target : event.target && event.target.parentElement;
    const root = rootForTarget(target);
    if (!root) return;

    const loadMore = target.closest('[data-abc-gift-load-more]');
    if (loadMore) {
      event.preventDefault();
      loadMoreGifts(root, loadMore);
      return;
    }

    const disabledGiftLink = target.closest("[data-abc-product-gift-link][aria-disabled='true']");
    if (disabledGiftLink) {
      event.preventDefault();
      event.stopPropagation();
      setNotice(root, "Free gift selected. One free gift per order.");
      return;
    }

    const giftOpen = target.closest("[data-abc-product-gift-open]");
    if (giftOpen) {
      event.preventDefault();
      openGiftModal(root);
      return;
    }

    const giftClose = target.closest("[data-abc-product-gift-close]");
    if (giftClose) {
      event.preventDefault();
      closeGiftModal(root);
      return;
    }

    const close = target.closest("[data-abc-add-more-product-close]");
    if (close) {
      event.preventDefault();
      closeModal(close.closest(MODAL_SELECTOR));
      return;
    }

    const trigger = target.closest("[data-abc-add-more-product-trigger]");
    if (trigger) {
      event.preventDefault();
      openModal(root, trigger);
      return;
    }

    const option = target.closest("[data-abc-add-more-option-value]");
    if (option) {
      event.preventDefault();
      if (option.classList.contains("is-unavailable")) return;

      const group = option.closest("[data-abc-add-more-option]");
      if (group) {
        group.querySelectorAll("[data-abc-add-more-option-value]").forEach((button) => {
          button.classList.remove("is-active");
          button.setAttribute("aria-pressed", "false");
        });
      }
      option.classList.add("is-active");
      option.setAttribute("aria-pressed", "true");
      updateModalState(option.closest(MODAL_SELECTOR));
      return;
    }

    const submit = target.closest("[data-abc-product-gift-submit]");
    if (submit) {
      event.preventDefault();
      addVariant(root, submit.dataset.selectedVariant, submit, submit.dataset.giftFreeMode === "true");
      return;
    }

    const direct = target.closest("[data-abc-add-more-direct]");
    if (direct && direct.matches("[data-abc-product-gift-button]")) {
      event.preventDefault();
      addVariant(root, direct.dataset.variantId, direct, direct.dataset.giftFreeMode === "true");
    }
  });

  document.addEventListener("cart:updated", (event) => refreshAll(event.detail && event.detail.cart));
  document.addEventListener("cart:refresh", (event) => {
    window.setTimeout(() => refreshAll(event.detail && event.detail.cart), 600);
  });
  document.addEventListener("DOMContentLoaded", () => refreshAll());
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    document.querySelectorAll(`${GIFT_MODAL_SELECTOR}.is-open`).forEach((modal) => {
      closeGiftModal(modal._abcGiftRoot || modal.closest(ROOT_SELECTOR));
    });
  });
  refreshAll();
})();
